import { DataContext } from '@/types';
import { Data } from '../Data';
import { User, UserData } from './User';

interface SwitchUserData extends UserData {
  isTrainingManager?: boolean;
  trainingManagerConfigurationTypeName?: string | null;
}

/**
 * The Switch user dialog's rows for ONE organization. Fetching per organization — rather than every
 * employee on the platform and filtering client-side — is what keeps this request off the 30s SQL
 * command timeout as the employee count grows.
 */
export class SwitchUserList extends Data {
  private static cache: Record<string, SwitchUserList> = {};

  users: User[] = [];

  constructor(options: any) {
    super({
      api: `/User/EmployeeIds?organizationId=${encodeURIComponent(options.organizationId)}`,
      callback: () => {
        // Cache only once the data is in, so a failed load for one organization does not permanently
        // resolve that organization to an empty list.
        SwitchUserList.cache[options.organizationId] = this;
        options.resolve({ data: this, cached: false });
      },
      ...options,
    });
  }

  onDataReceived(data: SwitchUserData[]) {
    // User drops the training-manager fields, so carry them across for EmployeeTable's TM badge.
    this.users = (data ?? []).map((d) =>
      Object.assign(new User({ data: d }), {
        isTrainingManager: d.isTrainingManager ?? false,
        trainingManagerConfigurationTypeName: d.trainingManagerConfigurationTypeName ?? '',
      })
    );
  }

  static getUsers(
    context: DataContext,
    organizationId: string
  ): Promise<{ data: SwitchUserList; cached: boolean }> {
    return new Promise((resolve, reject) => {
      const cached = SwitchUserList.cache[organizationId];
      if (cached) resolve({ data: cached, cached: true });
      else new SwitchUserList({ resolve, reject, context, organizationId });
    });
  }
}
